import React, { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import { Clock, DollarSign, BarChart2, Plus, X } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

const safeDate = d => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM, hh:mm a') : '-'; } catch { return '-'; } };
const minsToHrs = m => { const h = Math.floor(m/60); const min = m%60; return h > 0 ? `${h}h ${min}m` : `${min}m`; };

export default function TimeReportPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('time_report');
  const { formatDate, formatCurrency } = useSettings();
  const [data, setData] = useState({ logs: [], summary: [] });
  const [workers, setWorkers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [filters, setFilters] = useState({ worker_id: '', from_date: '', to_date: '' });
  const [loading, setLoading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({ worker_id: '', task_id: '', clock_in: '', clock_out: '' });
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    api.get('/workers').then(r => setWorkers(r.data));
    api.get('/tasks/all').then(r => {
      const uniqueTasks = Array.from(new Map(r.data.map(t => [`${t.task_title}-${t.project_name}`, t])).values());
      setTasks(uniqueTasks);
    }).catch(()=>{});
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.worker_id) params.worker_id = filters.worker_id;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const r = await api.get('/reports/time', { params });
      setData(r.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleManualSubmit = async () => {
    if (!manualForm.worker_id || !manualForm.task_id || !manualForm.clock_in || !manualForm.clock_out) {
      return toast.error('All fields are required');
    }
    const clockOut = new Date(manualForm.clock_out);
    const clockIn = new Date(manualForm.clock_in);
    if (clockOut <= clockIn) return toast.error('Clock-out time must be after clock-in time');
    
    setSavingManual(true);
    try {
      await api.post(`/tasks/${manualForm.task_id}/manual-time`, {
        clock_in: new Date(manualForm.clock_in).toISOString(),
        clock_out: new Date(manualForm.clock_out).toISOString(),
        notes: 'Manual entry by admin',
        worker_id: manualForm.worker_id  // Pass worker_id for admin entries
      });
      toast.success('Manual time entry added! ✅');
      setShowManualForm(false);
      setManualForm({ worker_id: '', task_id: '', clock_in: '', clock_out: '' });
      setFilteredTasks([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setSavingManual(false); }
  };

  const handleWorkerSelect = async (workerId) => {
    setManualForm({ ...manualForm, worker_id: workerId, task_id: '' });
    if (workerId) {
      try {
        // Fetch all tasks for this specific worker (direct + department assigned)
        const r = await api.get('/tasks/all', { params: { worker_id: workerId, status: 'all' } });
        setFilteredTasks(r.data || []);
      } catch {
        // Fallback to local filter
        const filtered = tasks.filter(t =>
          t.worker_id === parseInt(workerId) ||
          (t.dept_workers && t.dept_workers.some && t.dept_workers.includes(parseInt(workerId)))
        );
        setFilteredTasks(filtered);
      }
    } else {
      setFilteredTasks([]);
    }
  };

  const totalMins = data.summary.reduce((a,b) => a + (b.total_minutes || 0), 0);
  const totalEarnings = data.summary.reduce((a,b) => a + parseFloat(b.total_earnings || 0), 0);

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-4 sm:space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Worker Time Report</h1>
        <p className="text-gray-500 text-xs sm:text-sm">Hours worked and earnings per worker</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-3 sm:p-4 border border-gray-100 shadow-sm flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 sm:items-end">
        <div className="flex-1 sm:flex-initial">
          <label className="block text-xs font-medium text-gray-600 mb-1">Worker</label>
          <select value={filters.worker_id} onChange={e => setFilters({...filters, worker_id: e.target.value})}
            className="w-full border border-gray-200 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="">All Workers</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div className="flex-1 sm:flex-initial">
          <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
          <input type="date" value={filters.from_date} onChange={e => setFilters({...filters, from_date: e.target.value})}
            className="w-full border border-gray-200 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
        </div>
        <div className="flex-1 sm:flex-initial">
          <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
          <input type="date" value={filters.to_date} onChange={e => setFilters({...filters, to_date: e.target.value})}
            className="w-full border border-gray-200 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
        </div>
        <button onClick={load} className="bg-amber-600 hover:bg-amber-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium w-full sm:w-auto">
          🔍 Search
        </button>
        <button onClick={() => setShowManualForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2">
          <Plus className="w-4 h-4" /> Manual Entry
        </button>
      </div>

      {/* Manual Time Entry Modal */}
      {showManualForm && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:480,overflow:"hidden"}}>
            <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">Manual Time Entry</h2>
              <button onClick={() => setShowManualForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Worker *</label>
                <select value={manualForm.worker_id} onChange={e => handleWorkerSelect(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select Worker</option>
                  {workers.filter(w => w.is_active).map(w => <option key={w.id} value={w.id}>{w.name} {w.department_names ? `(${w.department_names})` : ''}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">Select a worker to see only their tasks</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Task *</label>
                <select value={manualForm.task_id} onChange={e => setManualForm({...manualForm, task_id: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{manualForm.worker_id ? 'Select Task' : 'Select a worker first'}</option>
                  {(manualForm.worker_id ? filteredTasks : tasks).map(t => (
                    <option key={t.id} value={t.id}>
                      [{t.proj_code || t.project_name}] {t.task_title} {t.stage_order > 0 ? `(S${t.stage_order})` : ''} — {t.status}
                    </option>
                  ))}
                </select>
                {manualForm.worker_id && filteredTasks.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">⚠️ No tasks assigned to this worker</p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Clock In *</label>
                <input type="datetime-local" value={manualForm.clock_in} onChange={e => setManualForm({...manualForm, clock_in: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Clock Out *</label>
                <input type="datetime-local" value={manualForm.clock_out} onChange={e => setManualForm({...manualForm, clock_out: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={handleManualSubmit} disabled={savingManual}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl font-medium text-sm disabled:opacity-50">
                  {savingManual ? 'Adding...' : 'Add Entry'}
                </button>
                <button onClick={() => setShowManualForm(false)}
                  className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2 rounded-xl font-medium text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Total Hours</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-1 sm:gap-2"><Clock className="w-4 sm:w-5 h-4 sm:h-5 text-blue-500 flex-shrink-0" />{minsToHrs(totalMins)}</p>
        </div>
        <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-1 sm:gap-2"><DollarSign className="w-4 sm:w-5 h-4 sm:h-5 text-green-500 flex-shrink-0" />{formatCurrency(totalEarnings.toFixed(0))}</p>
        </div>
        <div className="col-span-2 sm:col-span-1 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Sessions</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-1 sm:gap-2"><BarChart2 className="w-4 sm:w-5 h-4 sm:h-5 text-purple-500 flex-shrink-0" />{data.logs.length}</p>
        </div>
      </div>

      {/* Worker summary */}
      {data.summary.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-3 sm:px-5 py-3 sm:py-4 border-b"><h3 className="font-semibold text-gray-900 text-sm sm:text-base">Worker Summary</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>{['Worker','Hourly Rate','Sessions','Total Hours','Total Earnings'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-xs sm:text-sm">
                {data.summary.map(w => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-gray-900">{w.name}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600">{formatCurrency(w.hourly_rate)}/hr</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600">{w.sessions}</td>
                    <td className="px-4 py-3 text-sm font-medium text-blue-700">{minsToHrs(w.total_minutes || 0)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-700">{formatCurrency(parseFloat(w.total_earnings || 0).toFixed(0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed logs */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>
      ) : data.logs.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Detailed Logs ({data.logs.length})</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>{['Worker','Task','Stage','Dept','Proto Code','Project','Clock In','Clock Out','Duration','Earnings'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3 whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.logs.map(l => (
                  <tr key={l.id} className="hover:bg-gray-50 text-sm">
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{l.worker_name}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate" title={l.task_title}>{l.task_title}</td>
                    <td className="px-4 py-3 text-center">
                      {l.stage_order > 0 && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">S{l.stage_order}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{l.department_name || '—'}</td>
                    <td className="px-4 py-3">
                      {l.proto_code ? (
                        <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{l.proto_code}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{l.project_name}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{safeDate(l.clock_in)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{l.clock_out ? safeDate(l.clock_out) : <span className="text-green-600 font-medium animate-pulse">● Live</span>}</td>
                    <td className="px-4 py-3 text-blue-700 font-medium whitespace-nowrap">{l.duration_minutes ? minsToHrs(l.duration_minutes) : '—'}</td>
                    <td className="px-4 py-3 text-green-700 font-semibold whitespace-nowrap">{formatCurrency(parseFloat(l.earnings || 0).toFixed(0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-100">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No time logs found</p>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
